import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDatabase } from '@fortawesome/pro-solid-svg-icons';
import { connect } from 'react-redux';
import { isBoolean } from 'lodash';

import { isBlank, isPresent } from 'helpers/string_helpers';

import DateWithHover from 'components/timestamps/date_with_hover';
import DocAccessLevelSelect from 'bundles/leads/index/table/cells/doc_access_level';
import { getProject } from 'bundles/project/selectors/index';
import { DOC_ACCESS_LEVEL_FOR_LEASE, DOC_ACCESS_LEVEL_FOR_SALE } from 'bundles/leads/index/table/cell';
import formatPhoneNumber from 'helpers/formatters/advanced_phone_number_formats';

export class LeadInfo extends React.Component {
  static propTypes = {
    documentCenter: PropTypes.bool.isRequired,
    lead: PropTypes.shape({
      createdAt: PropTypes.date,
      contactCompany: PropTypes.string,
      contactCompanyPath: PropTypes.string,
      contactPath: PropTypes.string,
      contactPhoneNumber: PropTypes.string,
      contactType: PropTypes.string,
      displayName: PropTypes.string.isRequired,
      leadData: PropTypes.shape({
        email: PropTypes.string,
        jobTitle: PropTypes.string,
        phone: PropTypes.string
      }).isRequired,
      updatedAt: PropTypes.date,
      user: PropTypes.shape({
        name: PropTypes.string
      })
    }).isRequired,
    project: PropTypes.shape({
      leaseChecked: PropTypes.bool,
      saleChecked: PropTypes.bool
    })
  };

  render() {
    return (
      <React.Fragment>
        {this.renderPersonalInformation()}
        {this.renderJobTitle()}
        {this.renderCompanyRow()}
        {this.renderNewOtherFields()}
        {this.renderViewInDatabase()}
        {this.renderAuditFields()}
      </React.Fragment>
    );
  }

  renderCompanyRow() {
    const { lead: { contactCompany, displayName } } = this.props;
    if (isBlank(contactCompany) || contactCompany === displayName) return null;

    return (
      <div className="form-group" key="lead-pi-company" name="lead-pi-company">
        <label>Company</label>
        <p>{contactCompany}</p>
      </div>
    );
  }

  renderJobTitle() {
    const jobTitle = this.props.lead.leadData.jobTitle;
    if (isBlank(jobTitle)) return null;

    return (
      <div className="form-group" key="lead-pi-job-title" name="lead-pi-job-title">
        <label className="mb-0">Role / Job title</label>
        <p>{jobTitle}</p>
      </div>
    );
  }

  renderPersonalInformation() {
    // TODO: Fix formatting error caused by phony_formatted.
    // the phone value that comes from leadData is being formatted by phony_formatted in the serializer but it struggles
    // with some of the international numbers that could be input. To fix this we are now using the contactPhoneNumber
    // value that is formatted by the advanced_phone_number_formats helper.
    const { lead: { leadData: { email, phone } } } = this.props;
    const contactPhone = this.props.lead.contactPhoneNumber;

    const emailRow = (isPresent(email) &&
      <div className="form-group" key="lead-pi-email" name="lead-pi-email">
        <label className="mb-0">Email</label>
        <p>
          <a href={`mailto:${email}`}>{email}</a>
        </p>
      </div>
    );

    const phoneRow = (isPresent(phone) &&
      <div className="form-group" key="lead-pi-phone" name="lead-pi-phone">
        <label className="mb-0">{`Phone Number`}</label>
        <p>{formatPhoneNumber(contactPhone)}</p>
      </div>
    );

    const rowsToDisplay = [emailRow, phoneRow].filter(Boolean);

    if (rowsToDisplay.length === 0) {
      return null;
    } else {
      return (
        <React.Fragment>
          {rowsToDisplay}
        </React.Fragment>
      );
    }
  }

  renderDocsAccessLevelDropdowns() {
    const { lead, project } = this.props;

    const dropdownForLease = (project.leaseChecked &&
      <div className="form-group" key="lead-pi-doc-access-level-for-lease" name="lead-pi-doc-access-level-for-lease">
        <label className="mb-0">Doc Access Level for Lease</label>
        <DocAccessLevelSelect columnName={DOC_ACCESS_LEVEL_FOR_LEASE} lead={lead}  />
      </div>
    );

    const dropdownForSale = (project.saleChecked &&
      <div className="form-group" key="lead-pi-doc-access-level-for-sale" name="lead-pi-doc-access-level-for-sale">
        <label className="mb-0">Doc Access Level for Sale</label>
        <DocAccessLevelSelect columnName={DOC_ACCESS_LEVEL_FOR_SALE} lead={lead} />
      </div>
    );

    return (
      <React.Fragment>
        {dropdownForLease}
        {dropdownForSale}
      </React.Fragment>
    );
  }

  renderOldOtherFields() {
    const { lead: { listingLabel, is1031Profile }, project: { leaseChecked } } = this.props;
    const is1031Label = isBoolean(is1031Profile) ? (is1031Profile ? 'Yes' : 'No') : null;

    return (
      <React.Fragment>
        {leaseChecked && listingLabel && <div className="form-group" key="lead-pi-space" name="lead-pi-space">
          <label className="mb-0">Space</label>
          <p>{listingLabel}</p>
        </div>}
        <div className="form-group" key="lead-pi-1031-exchange" name="lead-pi-1031-exchange">
          <label className="mb-0">1031 Exchange</label>
          <p>{is1031Label}</p>
        </div>
      </React.Fragment>
    );
  }

  renderNewOtherFields() {
    const { lead: { is1031Profile, oneTimeLinkSentAt } } = this.props;
    const is1031Label = isBoolean(is1031Profile) ? (is1031Profile ? 'Yes' : 'No') : '-';
    const lastLinkSentAt = oneTimeLinkSentAt ? <DateWithHover timestamp={oneTimeLinkSentAt} /> : '-';

    return (
      <React.Fragment>
        <div className="form-group">
          <label>Invite to View Sent</label>
          <p>{lastLinkSentAt}</p>
        </div>
        <div className="form-group" key="lead-pi-1031-exchange" name="lead-pi-1031-exchange">
          <label className="mb-0">1031 Exchange</label>
          <p>{is1031Label}</p>
        </div>
      </React.Fragment>
    );
  }

  renderViewInDatabase() {
    const { lead } = this.props;
    if (isBlank(lead.contactPath)) return null;

    const links = [];

    if (lead.contactPath) {
      links.push(
        <div key="1">
          <a href={lead.contactPath} name="lead-view-in-database" rel="noopener noreferrer" target="_blank">
            <FontAwesomeIcon className="right-buffer-md text-muted" icon={faDatabase} />
            View {lead.displayName}
          </a>
        </div>
      );
    }

    if (lead.contactCompany && lead.contactCompanyPath && !lead.contactType === 'Contacts::Company') {
      links.push(
        <div key="2">
          <a href={lead.contactCompanyPath} rel="noopener noreferrer" target="_blank">
            <FontAwesomeIcon className="right-buffer-md text-muted" icon={faDatabase} />
            View {lead.contactCompany}
          </a>
        </div>
      );
    }

    if (links.length === 0) return null;

    return (
      <React.Fragment>
        <div className="form-group" key="lead-pi-database" name="lead-pi-database" >
          <label className="mb-0">Database</label>
          {links}
        </div>
      </React.Fragment>
    );
  }

  createdByUserName() {
    const { lead: { source, user } } = this.props;

    if (source === 'web_activity') return '';
    return `by ${user.name}`;
  }

  renderAuditFields() {
    const { lead: { createdAt, updatedAt } } = this.props;

    return (
      <React.Fragment>
        <div className="form-group" key="lead-pi-updated-at" name="lead-pi-updated-at">
          <label className="mb-0">Last Updated</label>
          <p><DateWithHover timestamp={updatedAt}/></p>
        </div>
        <div className="form-group" key="lead-pi-created-at" name="lead-pi-created-at">
          <label className="mb-0">Date Added</label>
          <p><DateWithHover timestamp={createdAt}/> {this.createdByUserName()}</p>
        </div>
      </React.Fragment>
    );
  }
}

const mapStateToProps = state => ({
  project: getProject(state),
});

export default connect(mapStateToProps)(LeadInfo);
